// Player settings, opened from the pause menu. Steppers and tap-to-cycle
// buttons rather than sliders: precise with a thumb, and they work the same
// when the game frame is rotated under iOS orientation lock.

import { addTapHaptic } from '../input/haptics';

export interface SettingRow {
  id: string;
  label: string;
  /** Small explanatory line under the label. */
  hint?: string;
  value: () => string;
  /** Stepper rows have both; a row with only `cycle` is a single tap-to-change button. */
  dec?: () => void;
  inc?: () => void;
  cycle?: () => void;
  /** Shown only while this returns true (e.g. controller rows with a controller connected). */
  visible?: () => boolean;
}

export interface FooterButton {
  label: string | (() => string);
  kind: 'primary' | 'secondary';
  run: () => void;
  /** Close the page after running (Done-style buttons). */
  closes?: boolean;
}

export class SettingsPage {
  private readonly el: HTMLElement;
  private readonly values = new Map<string, HTMLElement>();
  private readonly rowEls = new Map<string, HTMLElement>();
  open = false;
  onClose: (() => void) | undefined;

  private readonly footerButtons: Array<{ el: HTMLButtonElement; def: FooterButton }> = [];

  constructor(
    root: HTMLElement,
    private readonly rows: SettingRow[],
    footer: FooterButton[],
    title = 'Settings',
    className = '',
  ) {
    this.el = document.createElement('div');
    this.el.className = `overlay settings ${className}`;
    this.el.innerHTML = `<div class="menu-title">${title}</div><div class="settings-grid"></div><div class="settings-footer"></div>`;
    root.appendChild(this.el);
    this.el.addEventListener('pointerdown', (e) => e.stopPropagation());
    const grid = this.el.querySelector('.settings-grid') as HTMLElement;
    for (const row of rows) {
      const r = document.createElement('div');
      r.className = 'setting';
      r.innerHTML = `<div class="setting-label">${row.label}${row.hint ? `<small>${row.hint}</small>` : ''}</div><div class="setting-control"></div>`;
      const control = r.querySelector('.setting-control') as HTMLElement;
      const value = document.createElement('span');
      value.className = 'setting-value';
      this.values.set(row.id, value);
      this.rowEls.set(row.id, r);
      if (row.dec && row.inc) {
        control.append(this.button('−', 'step', row.dec, `${row.label}: less`), value, this.button('+', 'step', row.inc, `${row.label}: more`));
      } else if (row.cycle) {
        const b = this.button('', 'cycle', row.cycle, row.label);
        b.appendChild(value);
        control.append(b);
      }
      grid.appendChild(r);
    }
    const footerEl = this.el.querySelector('.settings-footer') as HTMLElement;
    for (const def of footer) {
      const el = this.button('', def.kind, () => {
        def.run();
        if (def.closes) this.close();
      });
      this.footerButtons.push({ el, def });
      footerEl.append(el);
    }
  }

  private button(text: string, kind: string, run: () => void, aria?: string): HTMLButtonElement {
    const b = document.createElement('button');
    b.className = `settings-btn ${kind}`;
    if (text) b.textContent = text;
    if (aria) b.setAttribute('aria-label', aria);
    b.addEventListener('click', () => {
      run();
      this.render();
    });
    addTapHaptic(b);
    return b;
  }

  render(): void {
    for (const { el, def } of this.footerButtons) {
      const text = typeof def.label === 'string' ? def.label : def.label();
      const node = el.firstChild;
      if (node && node.nodeType === Node.TEXT_NODE) node.textContent = text;
      else el.insertBefore(document.createTextNode(text), el.firstChild);
    }
    for (const row of this.rows) {
      const v = this.values.get(row.id);
      if (v) v.textContent = row.value();
      const el = this.rowEls.get(row.id);
      if (el && row.visible) el.style.display = row.visible() ? '' : 'none';
    }
  }

  show(): void {
    this.open = true;
    this.render();
    this.el.classList.add('show');
  }

  /** Hide without firing onClose (e.g. when the page starts something). */
  hide(): void {
    this.open = false;
    this.el.classList.remove('show');
  }

  close(): void {
    this.open = false;
    this.el.classList.remove('show');
    this.onClose?.();
  }
}
